import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ProjectDto } from 'src/app/core/state/project.store';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatMenuModule, MatIconModule, MatTooltipModule],
  selector: 'app-project-selector',
  styleUrl: './project-selector.component.scss',
  template: `
    @if (projects().length) {
      <button
        mat-button
        [matMenuTriggerFor]="menu"
        class="trigger-button"
        [matTooltip]="projectNameTooltip(selectedProject()?.name, selectedProjectNameMaxLength)"
        (menuOpened)="clearSearch()"
      >
        {{
          selectedProject()?.name
            ? truncateProjectName(selectedProject()?.name, selectedProjectNameMaxLength)
            : 'Select project'
        }}
        <div><mat-icon fontIcon="arrow_drop_down" /></div>
      </button>
      <mat-menu #menu="matMenu" class="project-menu">
        <div class="project-search" (click)="$event.stopPropagation()" (keydown)="$event.stopPropagation()">
          <mat-icon svgIcon="search" />

          <input
            type="text"
            placeholder="Search projects"
            [value]="searchTerm()"
            (input)="onSearch($event)"
          />
        </div>
        @for (project of filteredProjects(); track project.id) {
          <button
            class="project-menu-item {{ selectedProject()?.id === project.id ? 'selected' : undefined }}"
            mat-menu-item
            [attr.aria-label]="project.name"
            [matTooltip]="projectNameTooltip(project.name)"
            [matTooltipDisabled]="!isProjectNameTruncated(project.name)"
            (click)="onSelectedProject(project)"
          >
            {{ truncateProjectName(project.name) }}
          </button>
        }
      </mat-menu>
    } @else {
      <p>No projects</p>
    }
  `,
})
export class ProjectSelectorComponent {
  readonly projectNameMaxLength = 75;
  readonly selectedProjectNameMaxLength = 40;
  readonly projects = input.required<ProjectDto[]>();
  readonly selectedProject = input.required<ProjectDto | undefined>();

  readonly selected = output<ProjectDto | undefined>();
  readonly searchTerm = signal('');
  readonly filteredProjects = computed(() => {
    const search = this.searchTerm().trim().toLowerCase();

    if (!search) {
      return this.projects();
    }

    return this.projects().filter(project => project.name?.toLowerCase().includes(search) ?? false);
  });

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    this.searchTerm.set(input.value);
  }
  clearSearch() {
    this.searchTerm.set('');
  }
  onSelectedProject(project: ProjectDto | undefined) {
    this.selected.emit(project);
  }

  truncateProjectName(name: string | undefined, maxLength = this.projectNameMaxLength): string {
    if (!name) {
      return '';
    }

    if (name.length <= maxLength) {
      return name;
    }

    return `${name.slice(0, maxLength)}...`;
  }

  isProjectNameTruncated(name: string | undefined, maxLength = this.projectNameMaxLength): boolean {
    return (name?.length ?? 0) > maxLength;
  }

  projectNameTooltip(name: string | undefined, maxLength = this.projectNameMaxLength): string {
    return this.isProjectNameTruncated(name, maxLength) ? (name ?? '') : '';
  }
}
